import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  AudioWavesIcon,
  Edit04Icon,
  RecordIcon,
  UploadIcon,
  VideoIcon,
} from 'assets/icons';
import { useClickOutside } from 'hooks/click-outside';
import styles from './respond-menu.module.scss';

export type RespondMode =
  | 'upload'
  | 'journal'
  | 'audio'
  | 'video'
  | 'videocall';

// The modes answered in the recorder; Upload and Video Call open their own
// flows.
export type RecordMode = Exclude<RespondMode, 'upload' | 'videocall'>;

const OPTIONS: { mode: RespondMode; label: string; icon: React.ReactNode }[] = [
  { mode: 'upload', label: 'Upload', icon: <UploadIcon /> },
  { mode: 'journal', label: 'Journal', icon: <Edit04Icon /> },
  { mode: 'audio', label: 'Audio', icon: <AudioWavesIcon /> },
  { mode: 'video', label: 'Video', icon: <VideoIcon /> },
  { mode: 'videocall', label: 'Video Call', icon: <RecordIcon /> },
];

const MENU_WIDTH = 220;
const VIEWPORT_MARGIN = 16;

interface RespondMenuProps {
  // The prompt that was clicked; the menu opens just below it.
  anchor: DOMRect;
  onPick: (mode: RespondMode) => void;
  onClose: () => void;
}

// How to answer a prompt: the "New Memory" options. Looks like the "New" menu.
// Fixed to the page, so a scrolling or clipped container doesn't cut it off.
export const RespondMenu = ({ anchor, onPick, onClose }: RespondMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, onClose);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // It's placed once; scrolling would leave it behind, so it closes.
    const onScroll = () => onClose();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [onClose]);

  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(anchor.left, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN),
  );

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={styles.dropdown}
      style={{ top: anchor.bottom + 8, left, width: MENU_WIDTH }}
      role="menu"
      aria-label="Respond with"
    >
      <span className={styles.sectionHeader}>Respond with</span>
      {OPTIONS.map((option) => (
        <button
          type="button"
          role="menuitem"
          key={option.mode}
          className={styles.item}
          onClick={() => {
            onClose();
            onPick(option.mode);
          }}
        >
          <span className={styles.itemIcon}>{option.icon}</span>
          <span className={styles.itemLabel}>{option.label}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
};
